import { Reveal, SectionHeading } from "@/components/Reveal";
import { usePrayerTimes } from "@/hooks/usePrayerTimes";

const PrayerTile = ({ prayer, active, index }) => (
  <Reveal delay={index * 0.07} className="h-full">
    <div
      data-testid={`prayer-tile-${prayer.key}`}
      data-active={active}
      className={`relative flex h-full flex-col justify-between rounded-3xl border p-6 transition-[background-color,border-color] duration-500 ${
        active
          ? "animate-pulse-ring border-gold bg-gold text-navy"
          : "border-white/10 bg-white/[0.04] text-sand-50 hover:border-gold/40 hover:bg-white/[0.08]"
      }`}
    >
      <div className="flex items-start justify-between">
        <span className={`eyebrow ${active ? "text-navy/70" : "text-sand-50/60"}`}>{prayer.name}</span>
        <span className={`text-lg ${active ? "text-navy/70" : "text-sand-50/60"}`}>{prayer.arabic}</span>
      </div>
      <div className="mt-8">
        <div className="font-grotesk text-5xl font-extrabold tabular-nums tracking-tight">{prayer.time}</div>
        <div className={`mt-2 text-xs ${active ? "font-semibold text-navy/80" : "text-sand-50/40"}`}>
          {active ? "● Waktu sholat saat ini" : "WITA"}
        </div>
      </div>
    </div>
  </Reveal>
);

export const PrayerTimes = () => {
  const { timings, now, next, current, countdown, hijri, gregorian, source, isLoading, isError } = usePrayerTimes();

  return (
    <section id="jadwal" className="dark-surface relative overflow-hidden bg-navy py-28 text-sand-50 lg:py-36" data-testid="prayer-section">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 top-1/3 h-[36rem] w-[36rem] rounded-full bg-azure/50 blur-3xl" />
        <div className="absolute -right-24 -top-24 h-[24rem] w-[24rem] rounded-full border border-gold/20" />
        <div className="absolute -right-10 -top-10 h-[18rem] w-[18rem] rounded-full border border-gold/10" />
      </div>

      <div className="relative mx-auto grid max-w-[1400px] gap-14 px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <SectionHeading
            dark
            eyebrow="Jadwal Sholat · Desa Cupel, Jembrana"
            title={
              <>
                Waktu sholat <em>hari ini</em>
              </>
            }
          />
          <Reveal delay={0.2}>
            <div className="mt-10">
              <p className="font-display text-2xl italic text-gold md:text-3xl" data-testid="prayer-hijri-date">
                {hijri?.label ?? "—"}
              </p>
              <p className="mt-1 text-sm text-sand-50/60" data-testid="prayer-gregorian-date">
                {gregorian ?? ""}
              </p>
              <div className="mt-8 flex items-end gap-3">
                <span className="font-grotesk text-6xl font-extrabold tabular-nums tracking-tight lg:text-7xl" data-testid="wita-clock">
                  {now.label}
                </span>
                <span className="pb-2 text-sm text-sand-50/60">WITA</span>
              </div>
              {next && (
                <div className="mt-8 rounded-3xl border border-gold/30 bg-white/5 p-6 backdrop-blur" data-testid="next-prayer-countdown">
                  <p className="eyebrow text-gold">Menuju {next.name}</p>
                  <p className="mt-2 font-grotesk text-4xl font-bold tabular-nums">{countdown}</p>
                  <p className="mt-1 text-sm text-sand-50/60">
                    Adzan {next.name} pukul {next.time} WITA
                  </p>
                </div>
              )}
              <p className="mt-6 text-xs text-sand-50/40" data-testid="prayer-source-note">
                {isError
                  ? "Jadwal belum dapat dimuat. Coba muat ulang halaman."
                  : source === "fallback"
                    ? "Jadwal perkiraan—server jadwal sedang tidak terjangkau."
                    : "Metode Kementerian Agama RI · Koordinat Desa Cupel · Sumber: Aladhan"}
              </p>
            </div>
          </Reveal>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:col-span-7 xl:grid-cols-3" data-testid="prayer-grid">
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-44 animate-pulse rounded-3xl border border-white/10 bg-white/[0.04]" />
              ))
            : timings.map((t, i) => <PrayerTile key={t.key} prayer={t} index={i} active={current?.key === t.key} />)}
        </div>
      </div>
    </section>
  );
};
